import { useState, useEffect } from 'react';
import { Database, Play, RotateCcw, Plus, Trash2, AlertTriangle, CheckCircle2, X } from 'lucide-react';

const csrfToken = () => decodeURIComponent(
    document.cookie.split('; ').find(r => r.startsWith('XSRF-TOKEN='))?.split('=')[1] || ''
);

const apiHeaders = () => ({
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'X-XSRF-TOKEN': csrfToken(),
});

export default function KdsTestConsole() {
    const [status, setStatus] = useState(null);
    const [recepty, setRecepty] = useState([]);
    const [objednavky, setObjednavky] = useState([]);
    const [loading, setLoading] = useState(false);
    const [output, setOutput] = useState(null);

    // Formulář manuální objednávky
    const [showForm, setShowForm] = useState(false);
    const [formKanal, setFormKanal] = useState('rozvoz');
    const [formAdresa, setFormAdresa] = useState('Frenštát p. R., Náměstí Míru 1');
    const [formPolozky, setFormPolozky] = useState([{ recept_id: '', pocet: 1, pridavky: [] }]);

    const loadStatus = async () => {
        const r = await fetch('/api/kds-test/status', { headers: apiHeaders() });
        if (r.ok) setStatus(await r.json());
    };

    const loadRecepty = async () => {
        const r = await fetch('/api/recepty?typ=PRO', { headers: apiHeaders() });
        if (r.ok) {
            const data = await r.json();
            setRecepty(Array.isArray(data) ? data : (data.data || []));
        }
    };

    const loadObjednavky = async () => {
        const r = await fetch('/api/kds/stav', { headers: apiHeaders() });
        if (r.ok) {
            const data = await r.json();
            const all = [
                ...(data.prichozi || []),
                ...(data.solo_karty || []),
                ...((data.davka?.objednavky) || []),
                ...((data.cekajici || []).flatMap(d => d.objednavky || [])),
            ];
            setObjednavky(all.slice(0, 20));
        }
    };

    useEffect(() => {
        loadStatus();
        loadRecepty();
        loadObjednavky();
    }, []);

    const seedQuick = async (count, kanal) => {
        if (!confirm(`Vygenerovat ${count}× ${kanal} objednávek?`)) return;
        setLoading(true);
        setOutput(null);
        const r = await fetch('/api/kds-test/seed', {
            method: 'POST',
            headers: apiHeaders(),
            body: JSON.stringify({ count, kanal }),
        });
        const data = await r.json();
        setOutput({ ok: r.ok, text: data.output || data.error || 'Hotovo' });
        setLoading(false);
        await loadStatus();
        await loadObjednavky();
    };

    const reset = async () => {
        if (!confirm('VYMAZAT všechna testovací KDS data?')) return;
        setLoading(true);
        setOutput(null);
        const r = await fetch('/api/kds-test/reset', {
            method: 'POST',
            headers: apiHeaders(),
            body: JSON.stringify({}),
        });
        const data = await r.json();
        setOutput({ ok: r.ok, text: data.output || 'Hotovo' });
        setLoading(false);
        await loadStatus();
        await loadObjednavky();
    };

    const submitManual = async () => {
        const polozky = formPolozky.filter(p => p.recept_id);
        if (polozky.length === 0) { alert('Přidej alespoň 1 položku'); return; }
        setLoading(true);
        setOutput(null);
        const r = await fetch('/api/kds-test/vytvorit', {
            method: 'POST',
            headers: apiHeaders(),
            body: JSON.stringify({
                kanal: formKanal,
                adresa: formAdresa,
                polozky: polozky.map(p => ({
                    recept_id: parseInt(p.recept_id),
                    pocet: parseInt(p.pocet) || 1,
                    pridavky: (p.pridavky || []).filter(Boolean),
                })),
            }),
        });
        const data = await r.json();
        setOutput({ ok: r.ok, text: r.ok ? 'Vytvořeno' : (data.message || 'Chyba') });
        setLoading(false);
        if (r.ok) {
            setShowForm(false);
            setFormPolozky([{ recept_id: '', pocet: 1, pridavky: [] }]);
            await loadStatus();
            await loadObjednavky();
        }
    };

    const smazat = async (id) => {
        if (!confirm(`Smazat objednávku #${id}?`)) return;
        await fetch(`/api/kds-test/objednavky/${id}`, {
            method: 'DELETE',
            headers: apiHeaders(),
        });
        await loadStatus();
        await loadObjednavky();
    };

    const updatePolozku = (i, key, val) => {
        const next = [...formPolozky];
        next[i] = { ...next[i], [key]: val };
        setFormPolozky(next);
    };
    const addPolozku = () => setFormPolozky([...formPolozky, { recept_id: '', pocet: 1, pridavky: [] }]);
    const removePolozku = (i) => setFormPolozky(formPolozky.filter((_, idx) => idx !== i));
    const togglePridavek = (i, nazev) => {
        const next = [...formPolozky];
        const cur = next[i].pridavky || [];
        next[i].pridavky = cur.includes(nazev) ? cur.filter(x => x !== nazev) : [...cur, nazev];
        setFormPolozky(next);
    };

    const PRIDAVKY_NABIDKA = ['extra sýr', 'bez cibule', 'pikantní', 'extra omáčka', 'bez okurky'];

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-6">
            <header className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Database className="w-6 h-6" /> KDS Test Console
                    </h1>
                    <p className="text-sm opacity-70 mt-1">
                        Generování testovacích objednávek pro Kitchen Display System.
                    </p>
                </div>
            </header>

            {/* Status panel */}
            <section className="border rounded-lg p-4" style={{ borderColor: 'var(--border-base)' }}>
                <h2 className="font-semibold mb-3">Stav</h2>
                {status === null ? (
                    <p className="text-sm opacity-60">Načítám…</p>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                            <div className="opacity-60 text-xs uppercase">Test Mode</div>
                            <div className="font-mono mt-1">
                                {status.test_mode_aktivni ? (
                                    <span className="text-green-600 font-bold flex items-center gap-1">
                                        <CheckCircle2 className="w-4 h-4" /> ON
                                    </span>
                                ) : (
                                    <span className="text-amber-600 font-bold flex items-center gap-1">
                                        <AlertTriangle className="w-4 h-4" /> OFF
                                    </span>
                                )}
                            </div>
                        </div>
                        <div>
                            <div className="opacity-60 text-xs uppercase">Objednávek</div>
                            <div className="font-mono text-lg mt-1">{status.pocty.objednavky}</div>
                        </div>
                        <div>
                            <div className="opacity-60 text-xs uppercase">Položek</div>
                            <div className="font-mono text-lg mt-1">{status.pocty.polozky}</div>
                        </div>
                        <div>
                            <div className="opacity-60 text-xs uppercase">Dávek</div>
                            <div className="font-mono text-lg mt-1">{status.pocty.davky}</div>
                        </div>
                        <div className="col-span-2 md:col-span-4 text-xs opacity-60 font-mono pt-2 border-t" style={{ borderColor: 'var(--border-base)' }}>
                            Tabulky: {status.aktivni_tabulky.objednavky} · {status.aktivni_tabulky.polozky} · {status.aktivni_tabulky.davky}
                        </div>
                    </div>
                )}
                {status && !status.test_mode_aktivni && (
                    <div className="mt-3 p-3 rounded text-sm" style={{ background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)' }}>
                        Test mode je vypnutý — seed příkazy nepoběží. V <code>.env</code> nastav <code>KDS_TEST_MODE=true</code> a spusť <code>php artisan config:clear</code>.
                    </div>
                )}
            </section>

            {/* Quick actions */}
            <section className="border rounded-lg p-4" style={{ borderColor: 'var(--border-base)' }}>
                <h2 className="font-semibold mb-3">Quick actions</h2>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => seedQuick(5, 'mix')}
                        disabled={loading || !status?.test_mode_aktivni}
                        className="px-3 py-2 rounded border flex items-center gap-2 text-sm disabled:opacity-40 hover:bg-black/5"
                        style={{ borderColor: 'var(--border-base)' }}
                    >
                        <Play className="w-4 h-4" /> Seed 5 (mix)
                    </button>
                    <button
                        onClick={() => seedQuick(1, 'rozvoz')}
                        disabled={loading || !status?.test_mode_aktivni}
                        className="px-3 py-2 rounded border flex items-center gap-2 text-sm disabled:opacity-40 hover:bg-black/5"
                        style={{ borderColor: 'var(--border-base)' }}
                    >
                        <Play className="w-4 h-4" /> Seed 1 rozvoz
                    </button>
                    <button
                        onClick={() => seedQuick(1, 'bistro')}
                        disabled={loading || !status?.test_mode_aktivni}
                        className="px-3 py-2 rounded border flex items-center gap-2 text-sm disabled:opacity-40 hover:bg-black/5"
                        style={{ borderColor: 'var(--border-base)' }}
                    >
                        <Play className="w-4 h-4" /> Seed 1 bistro
                    </button>
                    <button
                        onClick={() => setShowForm(true)}
                        disabled={loading || !status?.test_mode_aktivni}
                        className="px-3 py-2 rounded border flex items-center gap-2 text-sm disabled:opacity-40 hover:bg-black/5"
                        style={{ borderColor: 'var(--border-base)' }}
                    >
                        <Plus className="w-4 h-4" /> Manuální objednávka…
                    </button>
                    <button
                        onClick={reset}
                        disabled={loading}
                        className="px-3 py-2 rounded border flex items-center gap-2 text-sm text-red-600 disabled:opacity-40 hover:bg-red-50"
                        style={{ borderColor: 'var(--border-base)' }}
                    >
                        <RotateCcw className="w-4 h-4" /> Reset test data
                    </button>
                </div>
                {output && (
                    <pre
                        className="mt-3 p-3 rounded text-xs font-mono overflow-x-auto whitespace-pre-wrap"
                        style={{ background: 'var(--surface-2, #f5f5f5)' }}
                    >
                        {output.text}
                    </pre>
                )}
            </section>

            {/* Tabulka objednávek */}
            <section className="border rounded-lg p-4" style={{ borderColor: 'var(--border-base)' }}>
                <h2 className="font-semibold mb-3">Aktivní KDS objednávky (max 20)</h2>
                {objednavky.length === 0 ? (
                    <p className="text-sm opacity-60">Žádné objednávky.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left opacity-60 text-xs uppercase">
                                    <th className="py-2 pr-2">Kód</th>
                                    <th className="py-2 pr-2">Kanál</th>
                                    <th className="py-2 pr-2">Stav</th>
                                    <th className="py-2 pr-2">Položek</th>
                                    <th className="py-2 pr-2">Adresa</th>
                                    <th className="py-2 text-right">Akce</th>
                                </tr>
                            </thead>
                            <tbody>
                                {objednavky.map(o => (
                                    <tr key={o.id} className="border-t" style={{ borderColor: 'var(--border-base)' }}>
                                        <td className="py-2 pr-2 font-mono">{o.kod}</td>
                                        <td className="py-2 pr-2">{o.kanal}</td>
                                        <td className="py-2 pr-2">{o.stav}</td>
                                        <td className="py-2 pr-2">{o.polozky?.length ?? 0}</td>
                                        <td className="py-2 pr-2 truncate max-w-xs">{o.adresa}</td>
                                        <td className="py-2 text-right">
                                            <button
                                                onClick={() => smazat(o.id)}
                                                className="text-red-600 hover:text-red-800 p-1"
                                                title="Smazat"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Manuální objednávka modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div
                        className="rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto"
                        style={{ background: 'var(--surface-1, #fff)' }}
                    >
                        <header className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border-base)' }}>
                            <h3 className="font-semibold">Nová testovací objednávka</h3>
                            <button onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button>
                        </header>
                        <div className="p-4 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <label className="block">
                                    <span className="text-sm opacity-70">Kanál</span>
                                    <select
                                        value={formKanal}
                                        onChange={e => setFormKanal(e.target.value)}
                                        className="mt-1 w-full border rounded px-2 py-1.5"
                                        style={{ borderColor: 'var(--border-base)', background: 'var(--surface-1)' }}
                                    >
                                        <option value="rozvoz">rozvoz</option>
                                        <option value="bistro">bistro</option>
                                        <option value="vydej">vydej</option>
                                    </select>
                                </label>
                                <label className="block">
                                    <span className="text-sm opacity-70">Adresa (rozvoz)</span>
                                    <input
                                        value={formAdresa}
                                        onChange={e => setFormAdresa(e.target.value)}
                                        disabled={formKanal !== 'rozvoz'}
                                        className="mt-1 w-full border rounded px-2 py-1.5 disabled:opacity-40"
                                        style={{ borderColor: 'var(--border-base)', background: 'var(--surface-1)' }}
                                    />
                                </label>
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-semibold">Položky</span>
                                    <button
                                        onClick={addPolozku}
                                        className="text-xs px-2 py-1 rounded border flex items-center gap-1"
                                        style={{ borderColor: 'var(--border-base)' }}
                                    >
                                        <Plus className="w-3 h-3" /> přidat
                                    </button>
                                </div>
                                <div className="space-y-3">
                                    {formPolozky.map((p, i) => (
                                        <div key={i} className="border rounded p-3 space-y-2" style={{ borderColor: 'var(--border-base)' }}>
                                            <div className="flex gap-2 items-end">
                                                <label className="block flex-1">
                                                    <span className="text-xs opacity-70">Produkt (PRO recept)</span>
                                                    <select
                                                        value={p.recept_id}
                                                        onChange={e => updatePolozku(i, 'recept_id', e.target.value)}
                                                        className="mt-1 w-full border rounded px-2 py-1.5"
                                                        style={{ borderColor: 'var(--border-base)', background: 'var(--surface-1)' }}
                                                    >
                                                        <option value="">— vyber —</option>
                                                        {recepty.map(r => (
                                                            <option key={r.id} value={r.id}>{r.nazev}</option>
                                                        ))}
                                                    </select>
                                                </label>
                                                <label className="block w-20">
                                                    <span className="text-xs opacity-70">Počet</span>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={p.pocet}
                                                        onChange={e => updatePolozku(i, 'pocet', e.target.value)}
                                                        className="mt-1 w-full border rounded px-2 py-1.5"
                                                        style={{ borderColor: 'var(--border-base)', background: 'var(--surface-1)' }}
                                                    />
                                                </label>
                                                {formPolozky.length > 1 && (
                                                    <button
                                                        onClick={() => removePolozku(i)}
                                                        className="text-red-600 p-2"
                                                        title="Odebrat"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                            <div>
                                                <span className="text-xs opacity-70">Ingredience</span>
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {PRIDAVKY_NABIDKA.map(nazev => {
                                                        const active = (p.pridavky || []).includes(nazev);
                                                        return (
                                                            <button
                                                                key={nazev}
                                                                onClick={() => togglePridavek(i, nazev)}
                                                                className={`text-xs px-2 py-1 rounded border ${active ? 'bg-blue-600 text-white border-blue-600' : ''}`}
                                                                style={!active ? { borderColor: 'var(--border-base)' } : {}}
                                                            >
                                                                {nazev}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <footer className="flex items-center justify-end gap-2 p-4 border-t" style={{ borderColor: 'var(--border-base)' }}>
                            <button
                                onClick={() => setShowForm(false)}
                                className="px-3 py-2 rounded border text-sm"
                                style={{ borderColor: 'var(--border-base)' }}
                            >
                                Zrušit
                            </button>
                            <button
                                onClick={submitManual}
                                disabled={loading}
                                className="px-3 py-2 rounded bg-blue-600 text-white text-sm disabled:opacity-40"
                            >
                                Vytvořit
                            </button>
                        </footer>
                    </div>
                </div>
            )}
        </div>
    );
}
